import memoize from 'lodash/memoize';
import React, {
  Children,
  Component,
  isValidElement,
  MouseEvent,
  MouseEventHandler,
  ReactElement,
  ReactNode,
} from 'react';
import { Icon as IconType } from '@tanium/react-graphics';
import { IconButton } from '@tanium/react-icon-button';
import { Menu, MenuItem, MenuItemProps } from '@tanium/react-menu';
import { SimpleTooltip } from '@tanium/react-tooltip';

export type GridIconActionProps = MenuItemProps;
export const GridIconAction = MenuItem;

export interface GridIconProps {
  /**
   * Icon to display.
   */
  icon: IconType;
  /**
   * Label used in the tooltip for the icon. Is also used for accessibility.
   */
  label: string;
  /**
   * Action items to show in a context menu for the icon.
   */
  children?: ReactElement<GridIconActionProps> | (ReactElement<GridIconActionProps> | undefined)[];
  /**
   * Click handler for the button
   */
  onClick?: MouseEventHandler;
}

const initialState = {
  isTooltipDisplayed: true,
};

type State = typeof initialState;

type RefObjectToMutableRefObject<T> = T extends React.RefObject<infer U>
  ? React.MutableRefObject<U>
  : never;

/**
 * There's a pesky little problem with using inline functions with refs and
 * react-popper.
 *
 * https://github.com/FezVrasta/react-popper/issues/199
 *
 * Memoizing on the handlers gets us around it.
 */
const handler = memoize((...h: React.Ref<HTMLElement | null>[]) => (r: HTMLElement | null) => {
  h.forEach((x) => {
    if (typeof x === 'function') {
      x(r);
    } else if (x) {
      // eslint-disable-next-line no-param-reassign -- we know that x is a ref here
      (x as RefObjectToMutableRefObject<typeof x>).current = r;
    }
  });
});

const extractChildProps = (child: ReactNode, i: number) =>
  isValidElement(child) && child.props && { ...child.props, key: i };

const toMenuItem = (props: MenuItemProps & { key: number }) => <MenuItem {...props} />;

const isMenuItemProps = (
  candidate: any, // eslint-disable-line @typescript-eslint/no-explicit-any
): candidate is MenuItemProps & { key: number } =>
  candidate?.onAction && candidate.key !== undefined;

/**
 * Component for handling actions in a grid. Behavior can be bound either to the click of the icon
 * or to items which display in a context menu. By default, the click behavior on the icon opens the
 * context menu if there are actions present.
 */
export class GridIcon extends Component<GridIconProps, State> {
  public static defaultProps = {
    onClick: () => {},
  };

  public readonly state: Readonly<State> = initialState;

  private handleHide = () => {
    this.setState({ isTooltipDisplayed: true });
  };

  public render() {
    const { icon: Icon, children, label, onClick } = this.props;

    const content = Children.toArray(children)
      .map(extractChildProps)
      .filter(isMenuItemProps)
      .map(toMenuItem);

    return (
      <Menu content={content.length > 0 && content} placement="bottom-end" onHide={this.handleHide}>
        {(menuArgs) => (
          <SimpleTooltip content={this.state.isTooltipDisplayed && label}>
            {(simpleTooltipArgs) => (
              <div ref={handler(simpleTooltipArgs.ref, menuArgs.ref)}>
                <IconButton
                  display="block"
                  icon={Icon}
                  label={label}
                  width={16}
                  height={16}
                  // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop
                  onClick={(e: MouseEvent) => {
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    onClick!(e);
                    if (content.length) {
                      this.setState({ isTooltipDisplayed: false });
                      menuArgs.show();
                    }
                  }}
                />
              </div>
            )}
          </SimpleTooltip>
        )}
      </Menu>
    );
  }
}
